class Cart extends HTMLElement {
  constructor() {
    super();

    this.cartToggle = document.querySelector("[data-cart-toggle]");
    this.navBackdrop = document.querySelector("[data-nav-backdrop]");
    this.cartLoader = this.querySelector(".cart__loader");
    this.cartDrawerIsOpen = false;

    const hash = location.hash.substr(1);

    if (hash === "cart") {
      this.cartDrawerIsOpen = true;
      this.open();
    }

    document.addEventListener("itemAdded", () => this.open(false));

    this.unsubscribeCartLoadingStart = subscribe(
      PUB_SUB_EVENTS.cartLoadingStart,
      this.showLoader.bind(this),
    );

    this.unsubscribeCartLoadingEnd = subscribe(
      PUB_SUB_EVENTS.cartLoadingEnd,
      this.hideLoader.bind(this),
    );
  }

  connectedCallback() {
    this.navBackdrop.addEventListener("click", () => {
      if (window.appState.cartDrawer) {
        this.close();
      }
    });

    this.cartToggle.addEventListener("click", this.toggleCart.bind(this));
    document.addEventListener("toggleMobileNav", () => this.close(false));
    document.addEventListener("toggleSearch", () => this.close(false));
    document.addEventListener("keydown", this.handleKeyDown.bind(this));

    if (window.location.hash === "#cart") {
      this.cartDrawerIsOpen = true;
      this.open();
    }

    this.addEventListener("cart:loading", () => this.showLoader());
    this.addEventListener("cart:done", () => this.hideLoader());
  }

  toggleCart(event) {
    event.preventDefault();
    if (this.cartDrawerIsOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(triggeredBy) {
    this.cartDrawerIsOpen = true;
    window.appState.cartDrawer = true;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add("active");
    document.body.classList.add("cart-open");
    const event = new Event("toggleCart");
    document.dispatchEvent(event);

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this;
        const focusElement = this.classList.contains("no-items")
          ? document.querySelector("button[type='submit']")
          : this.querySelector(".cart__checkout-button");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.dispatchEvent(
      new CustomEvent("activeDropdown", { bubbles: true }),
    );

    this.lockScroll();
  }

  close(includeUnlockScroll = true) {
    this.cartDrawerIsOpen = false;
    window.appState.cartDrawer = false;
    this.classList.remove("active");
    document.body.classList.remove("cart-open");
    document.dispatchEvent(
      new CustomEvent("closedDropdown", { bubbles: true }),
    );
    if (includeUnlockScroll) this.unlockScroll();
  }

  lockScroll() {
    if (document.body.style.position === "fixed") {
      return;
    }
    const scrollY = window.scrollY;
    document.documentElement.style.setProperty("--scroll-y", `${scrollY}px`);
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = `-${scrollY}px`;
  }

  unlockScroll() {
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    window.scrollTo(0, parseInt(scrollY || "0") * -1);
  }

  handleKeyDown(event) {
    // Check if the pressed key was the escape key
    if (event.key === "Escape" || event.key === "Esc" || event.keyCode === 27) {
      // If the cart drawer is open, close it
      if (this.cartDrawerIsOpen) {
        this.close();
      }
    }
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    this.renderButton();
  }

  renderButton() {
    fetch(window.Shopify.routes.root + "cart.js", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        const cartButton = document.querySelector("[data-cart-toggle]");

        if (!cartButton) return;
        if (response.item_count > 0) {
          cartButton.classList.add("header__cart-button--items");
        } else {
          cartButton.classList.remove("header__cart-button--items");
        }
        const hiddenCountSpan = cartButton.querySelector(
          "[data-cart-item-count]",
        );
        if (hiddenCountSpan) {
          hiddenCountSpan.innerHTML = response.item_count;
        }

        document
          .querySelector("[data-cart-toggle]")
          .classList.add("header__cart-button--ping");
        setTimeout(() => {
          document
            .querySelector("[data-cart-toggle]")
            .classList.remove("header__cart-button--ping");
        });
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  reRender() {
    fetch(`${window.location.pathname}?sections=cart-drawer`)
      .then((response) => response.json())
      .then((response) => {
        this.renderContents({
          sections: response,
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#cart",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  showLoader() {
    this.cartLoader?.removeAttribute("hidden");
    this.cartLoader
      ?.querySelector(".loading__spinner")
      ?.classList.remove("hidden");
  }
  hideLoader() {
    this.cartLoader?.setAttribute("hidden", true);
    this.cartLoader
      ?.querySelector(".loading__spinner")
      ?.classList.add("hidden");
  }

  disconnectedCallback() {
    if (this.unsubscribeCartLoadingStart) this.unsubscribeCartLoadingStart();
    if (this.unsubscribeCartLoadingEnd) this.unsubscribeCartLoadingEnd();
  }
}

customElements.define("cart-drawer", Cart);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.cart = document.querySelector("cart-drawer");
    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]'),
    ).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0,
    );

    this.debouncedOnChange = debounce((e) => {
      this.onChange(e);
    }, 300);

    this.addEventListener("change", this.debouncedOnChange.bind(this));
  }

  connectedCallback() {
    this.updateCartQualifiedAttribute();
    this.removeOfferItemIfNotQualified();
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value);
  }

  updateCartQualifiedAttribute() {
    fetch(`${routes.cart_url}.js`)
      .then((res) => res.json())
      .then((cart) => {
        const hasQualifyingItem = cart.items.some(
          (item) =>
            item.properties && item.properties._qualifying_offer_product,
        );

        return fetch(`${routes.cart_update_url}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attributes: {
              _cart_qualified_for_offer: hasQualifyingItem ? "true" : "",
            },
          }),
        });
      })
      .then(() => {
        this.removeOfferItemIfNotQualified();
      })
      .catch((error) => {
        console.error("Error updating cart attribute:", error);
      });
  }

  removeOfferItemIfNotQualified() {
    // Fetch the latest cart state
    fetch(`${routes.cart_url}.js`)
      .then((res) => res.json())
      .then((cart) => {
        // Check if the cart is NOT qualified for the offer
        if (
          cart.attributes &&
          cart.attributes._cart_qualified_for_offer !== "true"
        ) {
          // Find the offer item(s) in the cart
          const offerItem = cart.items.find(
            (item) =>
              item.properties && item.properties._cart_offer_item === "true",
          );
          if (offerItem) {
            // Remove the offer item by setting its quantity to 0
            publish(PUB_SUB_EVENTS.cartLoadingStart);
            return fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                id: offerItem.key,
                quantity: 0,
              }),
            })
              .then(() => {
                // Optionally, re-render the cart drawer
                this.cart.reRender();
              })
              .finally(() => {
                // End loader after removal (success or fail)
                publish(PUB_SUB_EVENTS.cartLoadingEnd);
              });
          }
        }
      })
      .catch((error) => {
        publish(PUB_SUB_EVENTS.cartLoadingEnd);
        console.error("Error removing offer item:", error);
      });
  }

  updateQuantity(line, quantity) {
    publish(PUB_SUB_EVENTS.cartLoadingStart);
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        document.querySelector("cart-drawer cart-items").innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections["cart-drawer"],
            "cart-drawer cart-items",
          );

        document.querySelector("cart-drawer .cart__gwl").innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections["cart-drawer"],
            "cart-drawer .cart__gwl",
          );

        document.querySelector("cart-drawer .cart__offer").innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections["cart-drawer"],
            "cart-drawer .cart__offer",
          );

        document.querySelector("cart-drawer [data-cart-item-count]").innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections["cart-drawer"],
            "cart-drawer [data-cart-item-count]",
          );

        document.querySelector(
          "cart-drawer .cart__free-shipping-wrapper",
        ).innerHTML = this.getSectionInnerHTML(
          parsedState.sections["cart-drawer"],
          "cart-drawer .cart__free-shipping-wrapper",
        );

        document.querySelector("cart-drawer .cart__bottom").innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections["cart-drawer"],
            "cart-drawer .cart__bottom",
          );

        document.querySelector("cart-drawer .cart__recommendations").innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections["cart-drawer"],
            "cart-drawer .cart__recommendations",
          );

        this.cart.renderButton();

        this.updateCartQualifiedAttribute();
        this.removeOfferItemIfNotQualified();
      })
      .finally(() => {
        publish(PUB_SUB_EVENTS.cartLoadingEnd);
      })
      .catch(() => {
        console.log(window.cartStrings.error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

customElements.define("cart-items", CartItems);

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initRecommendations();
  }

  initRecommendations() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("cart-recommendations");

        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("cart-recommendations", CartRecommendations);
